import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { DownloadOutlined, DownOutlined } from '@ant-design/icons';
import '@ant-design/compatible/assets/index.css';
import { Col, Button, Popconfirm, Dropdown, Menu } from 'antd';
import {
	assetsRestCrudThunksForSupplier,
	assetsSearchAndAPIAdaptor,
	cancelScheduledAssetTransfer,
	downloadAssetCSVForSupplier,
} from '../../thunks/assets_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import { ASSETS_CRUD_ACTION_CREATORS } from '../../actions/assets_actions';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import moment from 'moment';
import { downloadAssetNBVCSVForSupplier } from '../../thunks/asset_nbvs_thunks';
import AssetWithQRCodeDisplay from '../asset_row_display/AssetWithQRCodeDisplay';
import { getBuyerCompanySettingValue, PERMISSION_NAMES } from '../../utils/AuthUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { INITIAL_ACTIVE_FILTERS } from '../../utils/FilterUtils';
import ReduxDataTable from '../redux_data_table/ReduxDataTable';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { depreciationClassesRestCrudThunksForSupplier } from '../../thunks/depreciation_class_thunks';
import posthog from 'posthog-js';
import BulkTransferToStockLocationModal from './BulkTransferToStockLocationModal';
import BulkTransferToLocationModal from './BulkTransferToLocationModal';
import BulkTransferAssetsButton from '../assets_overview_page/BulkTransferAssetsButton';
import { useFilters } from '../../hooks/useFilters';
import { getSupplierAssetFilterConfig } from '../../utils/AssetFilterUtils';

const queryString = require('qs');

require('./SupplierAssetsIndexPage.less');

const ASSETS_OVERVIEW_TARGET_COLLECTION = 'assetsIndex';
const ASSETS_ADVANCED_FILTERS = 'assetsAdvancedFilters';

const SupplierAssetsIndexPage: FC<any> = ({
	history,
	location,
	assets,
	updateAssetsFilters,
	downloadAssetCSV,
	downloadAssetNBV,
	fetchAssets,
	cancelScheduledTransfer,
	fetchDepreciationClasses,
}) => {
	const currentUser = useSelector((state: any) => state.session.currentUser);
	const companyConfig = useSelector((state: any) => state.company_config.detail);

	const [showDepreciationClassFilter, setShowDepreciationClassFilter] = useState(false);
	const [nbvDownloading, setNbvDownloading] = useState(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
	const [selectedRows, setSelectedRows] = useState<any[]>([]);
	const [showTransferToStockLocation, setShowTransferToStockLocation] = useState(false);
	const [showTransferToLocation, setShowTransferToLocation] = useState(false);

	let searchString = location.search;
	if (searchString[0] === '?') {
		searchString = searchString.slice(1);
	}
	const queryParams = queryString.parse(searchString);
	const {
		current,
		pageSize,
		/* eslint-disable @typescript-eslint/no-unused-vars */
		total,
		sort_by,
		order,
		/* eslint-enable @typescript-eslint/no-unused-vars */
		...assetFilters
	} = queryParams;

	const initialPagination = useMemo(
		() => ({
			current: current ? parseInt(current) : 1,
			pageSize: pageSize
				? parseInt(pageSize)
				: posthog.isFeatureEnabled('assets-openwsearch')
				? 50
				: 10,
		}),
		[]
	);
	const initialFilters = useMemo(() => assetFilters || {}, []);

	useEffect(() => {
		history.push(`/supplier/assets/overview/all?${new URLSearchParams(location.search)}`);
	}, []);

	useEffect(() => {
		fetchDepreciationClasses({}, undefined, { pageSize: 1 }).then(
			(res) => nullSafeGet('length', res) > 0 && setShowDepreciationClassFilter(true)
		);
	}, []);

	const downloadCSV = useCallback(() => {
		const filters = assets[ASSETS_OVERVIEW_TARGET_COLLECTION].filters;
		downloadAssetCSV({}, filters);
	}, [downloadAssetCSV, assets]);

	const refreshAssets = useCallback(() => {
		const pagination = nullSafeGetOrElse(
			`${ASSETS_OVERVIEW_TARGET_COLLECTION}.pagination`,
			assets,
			{}
		);
		const sorting = nullSafeGetOrElse(`${ASSETS_OVERVIEW_TARGET_COLLECTION}.sorting`, assets, {});
		const filters = nullSafeGetOrElse(`${ASSETS_OVERVIEW_TARGET_COLLECTION}.filters`, assets, {});
		fetchAssets(filters, ASSETS_OVERVIEW_TARGET_COLLECTION, pagination, sorting, filters);
	}, [assets, fetchAssets]);

	const cancelTransfer = useCallback(() => {
		cancelScheduledTransfer({ assetIds: selectedRowKeys }).then(() => {
			setSelectedRowKeys([]);
			setSelectedRows([]);
			refreshAssets();
		});
	}, [cancelScheduledTransfer, selectedRowKeys, refreshAssets]);

	const downloadNBVCsv = useCallback(() => {
		const filters = assets[ASSETS_OVERVIEW_TARGET_COLLECTION].filters;
		setNbvDownloading(true);
		downloadAssetNBV({}, filters).finally(() => setNbvDownloading(false));
	}, [downloadAssetNBV, assets]);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				const path = `/supplier/assets/detail/${nullSafeGet('id', record)}/details`;
				history.push(
					getLinkWIthBackLinkParams(location, 'Back to Assets', path, {
						preserve: true,
						targetCollectionName: ASSETS_OVERVIEW_TARGET_COLLECTION,
					})
				);
			},
		}),
		[history, location]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				defaultSortOrder: 'ascend',
				render: (text, record) => <AssetWithQRCodeDisplay asset={record} />,
			},
		],
		[]
	);

	const allowArea = getBuyerCompanySettingValue(ROLE_TYPES.SUPPLIER)(
		'assetConfig.allowAreaAssociationWithAssets',
		{},
		currentUser,
		false
	);

	const removeAssetNumberToQRCodeAssociation = getBuyerCompanySettingValue(ROLE_TYPES.SUPPLIER)(
		'assetConfig.removeAssetNumberToQRCodeAssociation',
		{},
		currentUser,
		false
	);

	const updateFilters = useCallback(
		(filters) => updateAssetsFilters(filters, ASSETS_OVERVIEW_TARGET_COLLECTION),
		[updateAssetsFilters]
	);

	const allFilters = useFilters({
		filterTargetCollectionName: ASSETS_ADVANCED_FILTERS,
		overviewCollectionName: ASSETS_OVERVIEW_TARGET_COLLECTION,
		updateFilters,
	});

	const filterConfig = useMemo(
		() =>
			getSupplierAssetFilterConfig({
				filters: allFilters,
				allowArea,
				currentUser,
				showDepreciationClass: showDepreciationClassFilter,
				removeAssetNumberToQRCodeAssociation,
			}),
		[
			allFilters,
			allowArea,
			currentUser,
			showDepreciationClassFilter,
			removeAssetNumberToQRCodeAssociation,
		]
	);

	const hasScheduledSelected = selectedRows.some((r) => r.nextScheduledTransfer);
	const hasUnscheduledSelected = selectedRows.some((r) => !r.nextScheduledTransfer);

	const downloadMenu = (
		<Menu>
			<Menu.Item onClick={downloadCSV}>Export CSV</Menu.Item>
			<AccessPermissionChecker name={PERMISSION_NAMES.SHOW_ASSET_PRICE}>
				<Menu.Item onClick={downloadNBVCsv}>Net Book Value</Menu.Item>
			</AccessPermissionChecker>
		</Menu>
	);

	const transferMenu = (
		<Menu>
			<Menu.Item onClick={() => setShowTransferToLocation(true)}>To Location</Menu.Item>
			<Menu.Item onClick={() => setShowTransferToStockLocation(true)}>To Stock Location</Menu.Item>
		</Menu>
	);

	const rowSelection = {
		selectedRowKeys,
		onChange: (keys, rows) => {
			setSelectedRowKeys(keys);
			setSelectedRows(rows);
		},
		getCheckboxProps: (record) => {
			const wouldMix =
				selectedRowKeys.length > 0 &&
				((hasScheduledSelected && !record.nextScheduledTransfer) ||
					(hasUnscheduledSelected && record.nextScheduledTransfer));
			return {
				disabled: wouldMix,
				title: wouldMix ? "Scheduled and unscheduled assets can't be selected together" : undefined,
			};
		},
	};

	const fetchDataProp = useMemo(
		() =>
			posthog.isFeatureEnabled('assets-openwsearch')
				? {
						...assetsRestCrudThunksForSupplier,
						read: (
							params,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) =>
							assetsRestCrudThunksForSupplier.searchWithPostMethod(
								filters ? { ...(params || {}), ...filters } : params,
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection,
								assetsSearchAndAPIAdaptor
							),
				  }
				: assetsRestCrudThunksForSupplier,
		[]
	);

	return (
		<>
			<ReduxDataTable
				handleQueryParams
				filterConfig={filterConfig}
				entityCollectionName="assets"
				rowSelection={rowSelection}
				rightActions={
					<Col className={'flex items-baseline'} style={{ gap: 8 }}>
						{selectedRowKeys.length > 0 ? (
							<>
								<span className="bodyregular text-grey-8">{selectedRowKeys.length} selected</span>
								<Button
									size="large"
									type="link"
									onClick={() => {
										setSelectedRowKeys([]);
										setSelectedRows([]);
									}}
								>
									Clear selection
								</Button>
								{hasScheduledSelected ? (
									<Popconfirm
										title={`Cancel scheduled transfer for ${selectedRowKeys.length} asset(s)?`}
										onConfirm={cancelTransfer}
										okText="Cancel Transfer"
										cancelText="Keep"
										okButtonProps={{ danger: true }}
									>
										<Button size="large" danger type="primary">
											Cancel Transfer
										</Button>
									</Popconfirm>
								) : (
									<Dropdown overlay={transferMenu}>
										<Button size="large" type="primary">
											Transfer <DownOutlined />
										</Button>
									</Dropdown>
								)}
							</>
						) : (
							<>
								<Dropdown overlay={downloadMenu}>
									<Button key={1} size="large">
										<div className="flex flex-row">
											<DownloadOutlined />
										</div>
									</Button>
								</Dropdown>
								<BulkTransferAssetsButton onSuccess={refreshAssets} />
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_ASSETS}>
									<Button
										size="large"
										type="primary"
										onClick={() => history.push('/supplier/assets/overview/new')}
									>
										Add Asset
									</Button>
								</AccessPermissionChecker>
							</>
						)}
					</Col>
				}
				updateQueryParams={true}
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
								alt="Add your first asset"
							/>
						}
						headline={"It's a little lonely in here."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Looks like your team has not added any assets yet.
								</div>
								<Button
									type="primary"
									onClick={() => history.push('/supplier/assets/overview/new')}
								>
									Add your first asset
								</Button>
							</div>
						}
					/>
				}
				onTableRow={onRow}
				showHeader={false}
				targetCollectionName="assetsIndex"
				tableColumns={columns}
				keyAccessor={(el) => el.id}
				initialFilters={{ ...initialFilters, ...INITIAL_ACTIVE_FILTERS }}
				initialPagination={initialPagination}
				fetchData={fetchDataProp}
				stateSlice={assets}
				initialSorting={{ sort_by: 'name', order: 'ascend' }}
			/>
			{showTransferToStockLocation && (
				<BulkTransferToStockLocationModal
					assets={selectedRows}
					onCancel={() => setShowTransferToStockLocation(false)}
					onSuccess={() => {
						setSelectedRowKeys([]);
						setShowTransferToStockLocation(false);
						refreshAssets();
					}}
					initialValues={{ triggerAt: moment() }}
				/>
			)}
			{showTransferToLocation && (
				<BulkTransferToLocationModal
					assets={selectedRows}
					onCancel={() => setShowTransferToLocation(false)}
					onSuccess={() => {
						setSelectedRowKeys([]);
						setShowTransferToLocation(false);
						refreshAssets();
					}}
					initialValues={{ triggerAt: moment() }}
				/>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	assets: state.assets,
});

const mapDispatchToProps = (dispatch) => ({
	updateAssetsFilters: (filters, targetCollection) =>
		dispatch(ASSETS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	downloadAssetCSV: (params, filters) => dispatch(downloadAssetCSVForSupplier(params, filters)),
	downloadAssetNBV: (params, filters) => dispatch(downloadAssetNBVCSVForSupplier(params, filters)),
	fetchAssets: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			posthog.isFeatureEnabled('assets-openwsearch')
				? assetsRestCrudThunksForSupplier.searchWithPostMethod(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection,
						assetsSearchAndAPIAdaptor
				  )
				: assetsRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchDepreciationClasses: (params, targetCollectionName, pagination) =>
		dispatch(
			depreciationClassesRestCrudThunksForSupplier.readLite(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination
			)
		),
	cancelScheduledTransfer: (data) =>
		dispatch(cancelScheduledAssetTransfer(ROLE_TYPES.SUPPLIER)(data)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(SupplierAssetsIndexPage)
);
